import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";
import { getNavLinks } from "@/constants/navigation";
import { LOGO } from "@/constants/assets";
import { LanguageSelector } from "@/components/layout/language-selector";

// Header Component with Mobile Menu
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLanguage();
  const NAV_LINKS = getNavLinks(t);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#030014]/80 backdrop-blur-xl" : ""
        }`}
        data-testid="header"
      >
        <div className="max-w-[1800px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex items-center justify-between h-20 sm:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img
                src={LOGO}
                alt="Phuong Logo"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
            </Link>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <LanguageSelector />

              {/* Menu Button */}
              <button
                className="text-white/80 hover:text-white p-2 transition-colors"
                onClick={() => setIsMenuOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Glass Nav Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100]"
            data-testid="nav-menu-overlay"
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/70 backdrop-blur-md"
              onClick={() => setIsMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-2xl border-l border-white/10"
            >
              {/* Close Button */}
              <div className="flex justify-end p-6">
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-white/80 hover:text-white p-2 transition-colors"
                  data-testid="close-menu-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Nav Links */}
              <nav className="px-8 py-4" data-testid="mobile-nav">
                {NAV_LINKS.map((link, index) => {
                  const isExternal =
                    link.href.startsWith("http") ||
                    link.href.startsWith("mailto");
                  const Component = isExternal ? motion.a : motion(Link);
                  const linkProps = isExternal
                    ? {
                        href: link.href,
                        target: link.href.startsWith("http")
                          ? "_blank"
                          : undefined,
                        rel: link.href.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined,
                      }
                    : { to: link.href };

                  return (
                    <Component
                      key={link.label}
                      {...linkProps}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.08 }}
                      className="block py-5 text-xl text-white/70 hover:text-[#FF2E63] transition-colors border-b border-white/5"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {link.label}
                    </Component>
                  );
                })}

                {/* Download Button in Menu */}
                <motion.a
                  href="/#download"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="inline-flex items-center gap-3 mt-10 bg-[#FF2E63] text-white px-8 py-4 rounded-full font-semibold shadow-[0_0_30px_rgba(255,46,99,0.4)]"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid="menu-download-btn"
                >
                  <Download className="w-5 h-5" />
                  {t("nav.downloadApp")}
                </motion.a>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
